import type { RecurringException } from "@opus-dt/shared-types";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { WEEKDAYS } from "@/features/calendar/time-helpers";

interface Props {
  rule: RecurringException;
  disabled?: boolean;
  onChange: (rule: RecurringException) => void;
}

const ORDINALS = [
  { value: 1, label: "1ª" },
  { value: 2, label: "2ª" },
  { value: 3, label: "3ª" },
  { value: 4, label: "4ª" },
  { value: -1, label: "última" },
];

const MONTHS = [
  "janeiro",
  "fevereiro",
  "março",
  "abril",
  "maio",
  "junho",
  "julho",
  "agosto",
  "setembro",
  "outubro",
  "novembro",
  "dezembro",
];

// Os campos que só existem numa das três formas de regra. Vivem ao lado da linha
// que os compõe: são a metade que MUDA com `freq`, enquanto efeito, janela e motivo são a metade
// que não muda. Separá-los é o que mantém as duas legíveis.
export function RecurrenceFreqFields({ rule, disabled, onChange }: Props) {
  if (rule.freq === "MONTHLY") {
    return (
      <>
        <Select
          aria-label="Ocorrência no mês"
          className="h-9 w-[92px]"
          value={rule.ordinal}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...rule, ordinal: Number(e.target.value) as 1 | 2 | 3 | 4 | -1 })
          }
        >
          {ORDINALS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Dia da semana"
          className="h-9 w-[100px]"
          value={rule.weekday}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, weekday: Number(e.target.value) })}
        >
          {WEEKDAYS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.long}
            </option>
          ))}
        </Select>
        <span className="text-[12px] text-text-tertiary">do mês</span>
      </>
    );
  }

  if (rule.freq === "WEEKLY") {
    return (
      <Select
        aria-label="Dia da semana"
        className="h-9 w-[110px]"
        value={rule.weekday}
        disabled={disabled}
        onChange={(e) => onChange({ ...rule, weekday: Number(e.target.value) })}
      >
        {WEEKDAYS.map((d) => (
          <option key={d.value} value={d.value}>
            {d.long}
          </option>
        ))}
      </Select>
    );
  }

  return (
    <>
      <Input
        aria-label="Dia do mês"
        type="number"
        min={1}
        max={31}
        className="h-9 w-[72px]"
        placeholder="25"
        value={rule.day}
        disabled={disabled}
        onChange={(e) => onChange({ ...rule, day: Number(e.target.value) })}
      />
      <Select
        aria-label="Mês"
        className="h-9 w-[120px]"
        value={rule.month}
        disabled={disabled}
        onChange={(e) => onChange({ ...rule, month: Number(e.target.value) })}
      >
        {MONTHS.map((m, i) => (
          <option key={m} value={i + 1}>
            {m}
          </option>
        ))}
      </Select>
    </>
  );
}
